import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { collection, doc, onSnapshot, query, where } from 'firebase/firestore'
import { UpgradeModal, type PaywallKey } from '../components/Subscription/UpgradeModal'
import {
  WORKSPACE_TIER_CAP_KEYS,
  type WorkspaceTierCapKey,
  resolveTeamWorkspaceCaps,
} from '../config/planLimitsCore'
import {
  getTierLimit,
  normalizeTierId,
  type FeatureKey,
  type TierId,
} from '../services/subscriptionGuard'
import { firestoreDb } from '../services/firebase'
import { useWorkspace } from './useWorkspace'
import { SubscriptionContext, type SubscriptionContextValue } from './subscriptionContextCore'
import { effectiveEntitlementLimit, LIFETIME_PASSES, type LifetimeEntitlement } from '../config/lifetimeEntitlements'
import { isNoSignInPreviewActive } from '../debug'
import { limitsLiftedForWorkspace } from '../utils/demoUnlock'
import { useOptionalAuth } from './useAuth'
import {
  subscribePlanLimits,
  setPlanLimitOverridesForApp,
  usePlanLimitOverrides,
} from '../services/planLimitsService'

const FEATURE_KEYS: FeatureKey[] = [
  'workspaces', 'storageGB',
  'chatHistoryDays', 'chatMessageLimit', 'activeIntegrations', 'webhookCallsPerMonth',
  'meetingsPerMonth', 'auditLogRetentionDays', 'syncSpeedSeconds', 'aiChatHistories',
  'aiContextAttachments', 'maxSavedCharts',
]

const emptyUsage = () => Object.fromEntries(FEATURE_KEYS.map((key) => [key, 0])) as Record<FeatureKey, number>
const EMPTY_ENTITLEMENTS: LifetimeEntitlement[] = []

type PaywallState = { featureKey: PaywallKey; currentUsage: number; limit: number }
type SubscriptionSnapshot = { workspaceId: string; plan: TierId; usage: Record<FeatureKey, number>; reservedUsage: Record<FeatureKey, number>; workspaceReady: boolean; metricsReady: boolean }

export const SubscriptionProvider = ({ children }: { children: ReactNode }) => {
  const { activeWorkspaceId, workspaces } = useWorkspace()
  const activeWorkspace = workspaces.find((workspace) => workspace.id === activeWorkspaceId)
  const auth = useOptionalAuth()
  const currentUser = 'currentUser' in auth ? auth.currentUser : auth.user
  const isSignedIn = Boolean(currentUser?.uid) && !isNoSignInPreviewActive()
  const planLimitOverrides = usePlanLimitOverrides()

  const [snapshot, setSnapshot] = useState<SubscriptionSnapshot>(() => ({
    workspaceId: '', plan: 'FREE_TIER', usage: emptyUsage(), reservedUsage: emptyUsage(), workspaceReady: false, metricsReady: false,
  }))
  const [paywall, setPaywall] = useState<PaywallState | null>(null)
  const [entitlements, setEntitlements] = useState<LifetimeEntitlement[]>([])
  const isPreview = isNoSignInPreviewActive()
  const snapshotIsActive = snapshot.workspaceId === activeWorkspaceId
  const plan = isPreview ? normalizeTierId(activeWorkspace?.plan) : (snapshotIsActive ? snapshot.plan : normalizeTierId(activeWorkspace?.plan))
  const usage = (isPreview || !snapshotIsActive) ? emptyUsage() : snapshot.usage
  const reservedUsage = (isPreview || !snapshotIsActive) ? emptyUsage() : snapshot.reservedUsage
  const isLoading = !isPreview && Boolean(activeWorkspaceId) && (!snapshotIsActive || !snapshot.workspaceReady || !snapshot.metricsReady)
  const effectiveEntitlements = isPreview ? EMPTY_ENTITLEMENTS : entitlements

  useEffect(() => {
    if (!isSignedIn) return
    let hasLoggedError = false
    const unsubscribe = subscribePlanLimits(
      (overrides) => {
        setPlanLimitOverridesForApp(overrides)
      },
      (error) => {
        if (!hasLoggedError) {
          hasLoggedError = true
          console.warn('[SubscriptionProvider] Failed to load plan limit overrides:', error)
        }
      },
    )
    return () => unsubscribe()
  }, [isSignedIn])

  useEffect(() => {
    if (!activeWorkspaceId || isNoSignInPreviewActive()) return
    const updateSnapshot = (patch: Partial<Omit<SubscriptionSnapshot, 'workspaceId'>>) => setSnapshot((current) => ({
      ...(current.workspaceId === activeWorkspaceId
        ? current
        : { workspaceId: activeWorkspaceId, plan: normalizeTierId(activeWorkspace?.plan), usage: emptyUsage(), reservedUsage: emptyUsage(), workspaceReady: false, metricsReady: false }),
      ...patch,
    }))
    const unsubscribeWorkspace = onSnapshot(doc(firestoreDb, 'workspaces', activeWorkspaceId), (snapshot) => {
      const data = snapshot.data()
      setSnapshot((current) => {
        const base = current.workspaceId === activeWorkspaceId
          ? current
          : { workspaceId: activeWorkspaceId, plan: normalizeTierId(activeWorkspace?.plan), usage: emptyUsage(), reservedUsage: emptyUsage(), workspaceReady: false, metricsReady: false }
        return {
          ...base,
          plan: normalizeTierId(data?.plan),
          workspaceReady: true,
          usage: {
            ...base.usage,
            workspaces: Number(data?.limits?.workspacesCount ?? base.usage.workspaces) || 0,
            storageGB: Number(data?.limits?.storageUsedBytes ?? base.usage.storageGB * 1024 ** 3) / 1024 ** 3,
          },
        }
      })
    }, () => {
      updateSnapshot({ workspaceReady: true })
    })
    const unsubscribeMetrics = onSnapshot(collection(firestoreDb, 'workspace_usage', activeWorkspaceId, 'metrics'), (snapshot) => {
      const next = emptyUsage()
      const nextReserved = emptyUsage()
      snapshot.docs.forEach((metric) => {
        if (!FEATURE_KEYS.includes(metric.id as FeatureKey)) return
        const value = Number(metric.data().currentUsage ?? 0)
        next[metric.id as FeatureKey] = Number.isFinite(value) && value > 0 ? value : 0
        const reserved = Number(metric.data().reservedUsage ?? 0)
        nextReserved[metric.id as FeatureKey] = Number.isFinite(reserved) && reserved > 0 ? reserved : 0
      })
      setSnapshot((current) => ({
        ...(current.workspaceId === activeWorkspaceId
          ? current
          : { workspaceId: activeWorkspaceId, plan: normalizeTierId(activeWorkspace?.plan), usage: emptyUsage(), reservedUsage: emptyUsage(), workspaceReady: false, metricsReady: false }),
        usage: {
          ...next,
          workspaces: next.workspaces || (current.workspaceId === activeWorkspaceId ? current.usage.workspaces : 0),
          storageGB: next.storageGB || (current.workspaceId === activeWorkspaceId ? current.usage.storageGB : 0),
        },
        reservedUsage: nextReserved,
        metricsReady: true,
      }))
    }, () => {
      updateSnapshot({ metricsReady: true })
    })
    const unsubscribeEntitlements = onSnapshot(query(collection(firestoreDb, 'workspace_entitlements'), where('workspaceId', '==', activeWorkspaceId), where('type', '==', 'LIFETIME_PASS'), where('status', '==', 'ACTIVE')), (result) => {
      setEntitlements(result.docs.map((entry) => ({ id: entry.id, ...entry.data() }) as LifetimeEntitlement))
    }, () => setEntitlements([]))
    return () => {
      unsubscribeWorkspace()
      unsubscribeMetrics()
      unsubscribeEntitlements()
    }
  }, [activeWorkspace?.plan, activeWorkspaceId])

  const checkLimit = useCallback((featureKey: FeatureKey, requestedAmount = 1) => {
    if (limitsLiftedForWorkspace(activeWorkspace)) return true
    const planLimit = getTierLimit(plan, featureKey, undefined, planLimitOverrides)
    const entitlementLimit = effectiveEntitlementLimit(effectiveEntitlements.map((entry) => LIFETIME_PASSES[entry.passId]?.limits[featureKey]).filter((value): value is number => typeof value === 'number'))
    const limit = planLimit === -1 || entitlementLimit === -1 ? -1 : Math.max(planLimit, entitlementLimit)
    return limit === -1 || usage[featureKey] + reservedUsage[featureKey] + requestedAmount <= limit
  }, [activeWorkspace, effectiveEntitlements, plan, planLimitOverrides, reservedUsage, usage])

  useEffect(() => {
    const openPaywall = (event: Event) => {
      if (limitsLiftedForWorkspace(activeWorkspace)) return
      const detail = (event as CustomEvent<Record<string, unknown>>).detail ?? {}
      const requestedFeature = detail.featureKey
      const isCapKey = typeof requestedFeature === 'string' && WORKSPACE_TIER_CAP_KEYS.includes(requestedFeature as WorkspaceTierCapKey)
      const featureKey: PaywallKey = isCapKey
        ? requestedFeature as WorkspaceTierCapKey
        : FEATURE_KEYS.includes(requestedFeature as FeatureKey)
          ? requestedFeature as FeatureKey
          : 'activeIntegrations'
      const currentUsage = Number(detail.currentUsage ?? detail.activeCount ?? (isCapKey ? 0 : usage[featureKey as FeatureKey])) || 0
      const configuredLimit = isCapKey
        ? resolveTeamWorkspaceCaps(plan, planLimitOverrides)[featureKey as WorkspaceTierCapKey]
        : getTierLimit(plan, featureKey as FeatureKey, undefined, planLimitOverrides)
      const eventLimit = Number(detail.limit)
      setPaywall({ featureKey, currentUsage, limit: Number.isFinite(eventLimit) ? eventLimit : configuredLimit })
    }
    window.addEventListener('linkhub:paywall-required', openPaywall)
    return () => window.removeEventListener('linkhub:paywall-required', openPaywall)
  }, [activeWorkspace, plan, planLimitOverrides, usage])

  const value = useMemo<SubscriptionContextValue>(() => ({ plan, usage, reservedUsage, entitlements: effectiveEntitlements, isLoading, checkLimit }), [plan, usage, reservedUsage, effectiveEntitlements, isLoading, checkLimit])
  const openPlans = () => {
    setPaywall(null)
    window.history.pushState(null, '', '/purchase')
    window.dispatchEvent(new PopStateEvent('popstate'))
  }

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
      <UpgradeModal
        open={Boolean(paywall)}
        featureKey={paywall?.featureKey ?? 'workspaces'}
        plan={plan}
        currentUsage={paywall?.currentUsage ?? 0}
        limit={paywall?.limit ?? 0}
        onDismiss={() => setPaywall(null)}
        onUpgrade={openPlans}
      />
    </SubscriptionContext.Provider>
  )
}
